import { Component, inject, OnInit } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Customer } from 'src/app/classes/customer.class';
import { CustomerService } from '../../../../services/customer.service';
import { InputComponent } from '../input/input.component';

@Component({
    selector: 'app-customer-input',
    standalone: false,
    templateUrl: './customer-input.component.html',
    styleUrl: './customer-input.component.scss',
})
export class CustomerInputComponent extends InputComponent implements OnInit {
    private customerService = inject(CustomerService);

    customers: Customer[] = [];
    customersLoading = false;

    customerInput$ = new Subject<string>();
    customerFocus$ = new Subject<void>();

    ngOnInit() {
        this.setupAutoComplete();
        this.setupFocusChanges();
        this.loadInitialCustomerSelection();
        this.setupValueChanges();
    }

    setupValueChanges() {
        this.subscriptions.add(
            this.fc.valueChanges.subscribe(customerId => {
                this.ensureCustomerLoaded(customerId);
            })
        );
    }

    ensureCustomerLoaded(customerId?: string) {
        if (!customerId?.length) {
            return;
        }

        if (this.customers.some(customer => customer.afas_customer_id === customerId)) {
            return;
        }

        this.subscriptions.add(
            this.searchCustomers(customerId).subscribe(customers => {
                const match = customers.find(customer => customer.afas_customer_id === customerId);
                if (
                    match &&
                    this.customers.some(customer => customer.afas_customer_id === match.afas_customer_id) === false
                ) {
                    this.customers = [match, ...this.customers];
                }
            })
        );
    }

    setupAutoComplete() {
        this.subscriptions.add(
            this.customerInput$
                .pipe(
                    debounceTime(200),
                    distinctUntilChanged(),
                    switchMap(term => this.searchCustomers(term))
                )
                .subscribe(customers => {
                    this.customers = customers;
                })
        );
    }

    setupFocusChanges() {
        this.subscriptions.add(
            this.customerFocus$.pipe(switchMap(() => this.searchCustomers(''))).subscribe(customers => {
                this.customers = customers;
            })
        );
    }

    loadInitialCustomerSelection() {
        const customerId = this.fc.value as string;
        if (!customerId?.length) {
            return;
        }

        this.subscriptions.add(
            this.searchCustomers(customerId).subscribe(customers => {
                this.customers = customers;
            })
        );
    }

    searchCustomers(search: string): Observable<Customer[]> {
        this.customersLoading = true;

        return this.customerService.search(search).pipe(
            switchMap(customers => {
                this.customersLoading = false;
                return of(customers);
            }),
            catchError(() => {
                this.customersLoading = false;
                return of([]);
            })
        );
    }

    trackByFn(item: Customer) {
        return item.afas_customer_id;
    }
}
